import { H5 } from '../Text';
import { classNames } from 'utils/classNames';
import { IButtonProps } from './Button';

export interface ISwitchButtonProps extends IButtonProps {
  textClassName?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  className?: string;
  textLeft?: boolean;
  disabled?: boolean;
  // A disabled switch that says why: it stays focusable, a tap or key press
  // calls this, and the reason's element id is read out with it (WHIT-494).
  onDisabledClick?: () => void;
  describedBy?: string;
}

export const SwitchButton: React.FC<ISwitchButtonProps> = ({
  className,
  children,
  textClassName,
  value,
  onChange,
  textLeft,
  disabled = false,
  onDisabledClick,
  describedBy,
}) => {
  return (
    // The whole switch, text included, is the tap target (WHIT-466). It has no
    // label-for or id: every switch shared the id "toggle", so a tap on
    // one knob clicked the first #toggle on the page and flipped that switch.
    <div
      role="switch"
      aria-checked={value}
      aria-disabled={disabled}
      aria-describedby={describedBy}
      tabIndex={disabled && !onDisabledClick ? -1 : 0}
      onClick={e => {
        e.preventDefault();
        if (disabled) {
          onDisabledClick?.();
          return;
        }
        onChange(!value);
      }}
      onKeyDown={e => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        if (disabled && !onDisabledClick) return;
        e.preventDefault();
        // Holding the key down repeats keydown; one press is one toggle.
        if (e.repeat) return;
        if (disabled) {
          onDisabledClick?.();
          return;
        }
        onChange(!value);
      }}
      className={`${className || ''} rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-tt-dark-purple ${
        // Off and unavailable must not look alike.
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      }`}>
      {textLeft && (
        <H5
          as="span"
          className={classNames(
            'pr-2 flex items-center max-w-[12.5rem] xs:max-w-full',
            textClassName,
          )}>
          {children}
        </H5>
      )}
      <div className="flex items-center w-[1.75rem]">
        <div
          className={`relative w-8 h-4 transition duration-200 ease-linear rounded-full cursor-pointer
            ${
              value === true
                ? disabled
                  ? 'bg-gray-400'
                  : 'bg-tt-yellow'
                : 'bg-gray-400'
            }
          `}>
          <span
            className={`absolute left-0 w-4 h-4 mb-2 transition duration-100 ease-linear transform bg-white border-2 rounded-full cursor-pointer
              ${
                value === true
                  ? disabled
                    ? 'translate-x-full border-gray-400'
                    : 'translate-x-full border-tt-yellow'
                  : 'translate-x-0 border-gray-400'
              }
            `}></span>
        </div>
      </div>
      {!textLeft && (
        <H5
          as="span"
          className={classNames(
            'pl-2 flex items-center max-w-[12.5rem] xs:max-w-full',
            textClassName,
          )}>
          {children}
        </H5>
      )}
    </div>
  );
};
